/**
 * Main Resources — client-side type filters
 */
(() => {
  'use strict';

  const QUERY_KEY = 'type';
  let activeInstance = null;

  function getQueryType() {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get(QUERY_KEY) || 'all').toLowerCase();
    } catch (e) {
      return 'all';
    }
  }

  function setQueryType(handle) {
    try {
      const url = new URL(window.location.href);
      if (!handle || handle === 'all') {
        url.searchParams.delete(QUERY_KEY);
      } else {
        url.searchParams.set(QUERY_KEY, handle);
      }
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* ignore */
    }
  }

  class MainResources {
    constructor(root) {
      this.root = root;
      if (!this.root) return;

      this.grid = this.root.querySelector('[data-resources-grid]');
      this.cards = Array.from(this.root.querySelectorAll('[data-resources-grid] > [data-type]'));
      this.filters = Array.from(this.root.querySelectorAll('[data-resources-filter-pill]'));
      this.statusEl = this.root.querySelector('[data-filter-status]');
      this.emptyEl = this.root.querySelector('[data-filter-empty]');
      this.onFilterClick = this.handleFilterClick.bind(this);
      this.onPopState = this.handlePopState.bind(this);

      this.init();
    }

    init() {
      if (!this.grid || this.filters.length === 0) return;

      this.filters.forEach((btn) => btn.addEventListener('click', this.onFilterClick));
      window.addEventListener('popstate', this.onPopState);

      let initial = getQueryType();
      if (!this.isValidFilter(initial)) initial = 'all';
      this.applyFilter(initial, { skipUrl: true });
    }

    destroy() {
      this.filters.forEach((btn) => btn.removeEventListener('click', this.onFilterClick));
      window.removeEventListener('popstate', this.onPopState);
    }

    isValidFilter(handle) {
      if (handle === 'all') return true;
      return this.filters.some((btn) => btn.getAttribute('data-filter-type') === handle);
    }

    handleFilterClick(event) {
      const handle = event.currentTarget.getAttribute('data-filter-type') || 'all';
      this.applyFilter(handle, { skipUrl: false });
    }

    handlePopState() {
      let handle = getQueryType();
      if (!this.isValidFilter(handle)) handle = 'all';
      this.applyFilter(handle, { skipUrl: true });
    }

    applyFilter(handle, options) {
      options = options || {};
      const type = handle || 'all';
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const match = type === 'all' || card.getAttribute('data-type') === type;
        card.hidden = !match;
        if (match) visibleCount += 1;
      });

      this.syncFilterButtons(type);
      this.updateStatus(visibleCount);
      this.toggleEmptyState(visibleCount === 0 && this.cards.length > 0);
      this.refreshGlassActivePill();

      if (!options.skipUrl) setQueryType(type);
    }

    syncFilterButtons(handle) {
      this.filters.forEach((btn) => {
        const isActive = btn.getAttribute('data-filter-type') === handle;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) {
          btn.setAttribute('data-orbe-glass', '');
        } else {
          btn.removeAttribute('data-orbe-glass');
          btn.removeAttribute('data-orbe-glass-tier');
          const surface = btn.querySelector('[data-orbe-glass-surface]');
          if (surface) surface.style.filter = '';
        }
      });
    }

    refreshGlassActivePill() {
      if (!window.OrbeGlass) return;
      this.filters.forEach((btn) => {
        if (btn.classList.contains('is-active')) {
          btn.setAttribute('data-orbe-glass', '');
          if (window.OrbeGlass.destroy) window.OrbeGlass.destroy(btn);
          if (window.OrbeGlass.init) window.OrbeGlass.init(btn);
        } else if (window.OrbeGlass.destroy) {
          window.OrbeGlass.destroy(btn);
        }
      });
    }

    updateStatus(count) {
      if (!this.statusEl) return;
      const template =
        this.statusEl.getAttribute('data-template') || 'Showing __COUNT__ resources';
      this.statusEl.textContent = template.split('__COUNT__').join(String(count));
    }

    toggleEmptyState(show) {
      if (this.emptyEl) this.emptyEl.hidden = !show;
      if (this.grid) this.grid.hidden = show;
    }
  }

  function boot(scope) {
    if (activeInstance) {
      activeInstance.destroy();
      activeInstance = null;
    }
    const root =
      (scope && scope.querySelector && scope.querySelector('[data-resources-root]')) ||
      document.querySelector('[data-resources-root]');
    if (!root) return;
    activeInstance = new MainResources(root);
  }

  document.addEventListener('DOMContentLoaded', () => boot(document));

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.querySelector('[data-resources-root]')) {
      boot(event.target);
    }
  });
})();
